import React, { useCallback, useLayoutEffect, useState } from 'react';
import './RegisterMerchant.scss';
import MerchantForm from 'components/MerchantPortal/MerchantForm/MerchantForm';
import Breadcrumbs from 'components/Shared/Breadcrumbs/Breadcrumbs';
import { FlightButton } from '@flybits/design-system';
import MerchantReview from 'components/MerchantPortal/MerchantReview/MerchantReview';
import MerchantSubmitted from 'components/MerchantPortal/MerchantSubmitted/MerchantSubmitted';
import { MerchantFormValues, MerchantsRouteParams } from '../merchant-portal.types';
import UnsavedPrompt from 'components/Shared/UnsavedPrompt/UnsavedPrompt';
import useMerchantCategoriesMock from 'hooks/useMerchantCategoriesMock';
import useDelayedLoadingState from 'hooks/useDelayedLoading';
import { useHistory, useParams } from 'react-router-dom';
import merchantService from 'services/api/merchants.api';
import { mapMerchantDataToFormValues, mapMerchantFormValuesToData } from '../merchant-portal.helpers';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import { MERCHANT_FORM_INIT_VALS } from '../merchant-portal.constants';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { capitalizeFirstCharacter } from 'helpers/common.helper';

const REGISTER_MERCHANT = 'register-merchant';
const CLASSES = {
  HEADER: `${REGISTER_MERCHANT}__header`,
  INFO: `${REGISTER_MERCHANT}__header__info`,
  TITLE: `${REGISTER_MERCHANT}__header__title`,
  GUTTER: `${REGISTER_MERCHANT}__header__gutter`,
  SUBTITLE: `${REGISTER_MERCHANT}__header__subtitle`,
  BODY: `${REGISTER_MERCHANT}__body`,
  BREADCRUMBS: `${REGISTER_MERCHANT}__body__breadcrumbs`,
  CONTAINER: `${REGISTER_MERCHANT}__body__container`,
};

// NOTE: These are EXAMPLE icons. Please update to match with designs when implementing
const BREADCRUMB_ITEMS = [
  { icon: null, forceOutline: true, text: 'Create Merchant profile' },
  { icon: null, forceOutline: true, text: 'Review and confirm' },
];

const initialValues: MerchantFormValues = MERCHANT_FORM_INIT_VALS;
const RegisterMerchant = function () {
  const history = useHistory();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [selectedStep, setSelectedStep] = useState(1);
  const [submitErrorMessage, setSubmitErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isDelayedSubmitting = useDelayedLoadingState(isSubmitting);
  const { merchantCategories } = useMerchantCategoriesMock();
  const [merchantData, setMerchantData] = useState<MerchantFormValues>(initialValues);
  const { pid: projectId } = useParams<MerchantsRouteParams>();
  const [isMerchantFormDirty, setMerchantFormDirty] = useState(false);
  const handleOnFormSubmit = useCallback((values: MerchantFormValues) => {
    setSelectedStep(2);
    setMerchantData(values);
  }, []);

  const handleOnReviewSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setSubmitErrorMessage('');
    setSelectedStep(3);
    try {
      const merchant = await merchantService.createMerchant(await mapMerchantFormValuesToData(merchantData));
      setMerchantData(mapMerchantDataToFormValues(merchant));
      await queryClient.invalidateQueries({ queryKey: ['merchants'] });
      setIsSubmitting(false);
    } catch (e: any) {
      let errorMessage = isAxiosError(e) || e instanceof Error ? e.message : 'unknown';
      // handles better error message from backend
      if (e?.response?.data?.error?.exceptionMessage) {
        errorMessage = e.response.data.error.exceptionMessage;
      }
      setSubmitErrorMessage(errorMessage);
      setIsSubmitting(false);
    }
  }, [merchantData, queryClient]);
  const handleResetForm = useCallback(() => {
    setMerchantData(initialValues);
    setSelectedStep(1);
    setSubmitErrorMessage('');
  }, []);

  useLayoutEffect(() => {
    if (!isDelayedSubmitting && submitErrorMessage) {
      setSelectedStep(2);
      dispatch({
        type: 'SHOW_SNACKBAR',
        payload: {
          title: 'Error',
          content: `The Merchant "${merchantData.name}" could not be created. ${capitalizeFirstCharacter(
            submitErrorMessage,
          )}`,
          type: 'error',
        },
      });
    }
  }, [dispatch, isDelayedSubmitting, merchantData.name, submitErrorMessage]);

  return (
    <>
      <div className={REGISTER_MERCHANT}>
        <header className={CLASSES.HEADER}>
          <FlightButton
            iconLeft="clear"
            theme="link"
            ariaLabel="navigate back to merchant list"
            onClick={() => history.push(`/project/${projectId}/merchants`)}
          />
          <div className={CLASSES.TITLE}>Register a Local Merchant</div>
          <div className={CLASSES.GUTTER}></div>
        </header>
        {selectedStep !== 3 ? (
          <div className={CLASSES.BODY}>
            <section className={CLASSES.BREADCRUMBS}>
              <Breadcrumbs items={BREADCRUMB_ITEMS} selectedStep={selectedStep} />
            </section>
            {selectedStep === 1 ? (
              <section className={CLASSES.CONTAINER}>
                <MerchantForm
                  data={merchantData}
                  categories={merchantCategories ?? []}
                  onSubmit={handleOnFormSubmit}
                  setDirty={setMerchantFormDirty}
                />
              </section>
            ) : (
              <section className={CLASSES.CONTAINER}>
                <MerchantReview
                  data={merchantData}
                  onBackClick={() => setSelectedStep(1)}
                  onSubmit={handleOnReviewSubmit}
                />
              </section>
            )}
          </div>
        ) : (
          <MerchantSubmitted onReset={handleResetForm} data={merchantData} isSubmitting={isDelayedSubmitting} />
        )}
      </div>
      <UnsavedPrompt
        when={isMerchantFormDirty && selectedStep < 3}
        unblockPaths={['/register-merchant']}
        dialogProps={{
          description: 'This will undo all the information you have entered since you started.',
        }}
      />
    </>
  );
};

export default RegisterMerchant;
